import styles from './price.module.sass';
import cn from 'classnames';
import { DetailedHTMLProps, HtmlHTMLAttributes } from 'react';
import { FontIcon } from '../FontIcon';

/** Extracts numeric value from strings like "25 SAR/month" or numbers, formats with 2 decimals (e.g. "25.00") */
const formatPriceValue = (value: string | number | undefined): string => {
    if (value === undefined || value === null) return '';
    const str = typeof value === 'number' ? String(value) : value;
    if (!str) return '';
    const match = str.replace(/[^\d.]/g, '').match(/[\d.]+/);
    if (!match) return '';
    const num = parseFloat(match[0]);
    if (isNaN(num)) return '';
    return num.toFixed(2);
};

export interface PriceProps extends DetailedHTMLProps<HtmlHTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    free?: boolean;
    price?: string | number;
    oldPrice?: string;
    currency?: string;
    description?: string | string[] | React.ReactElement | React.ReactElement[];
    isStartAligned?: boolean;
    freeLabel?: string;
    includedLabel?: string;
    percentage?: string | number;
    prefix?: string;
    suffix?: string;
    shouldShowSarSymbol?: boolean;
    freePriceOrText?: string;
}

export const Price = ({
    free,
    price,
    oldPrice = '0.00',
    currency,
    description,
    isStartAligned = false,
    freeLabel = 'free',
    includedLabel,
    percentage = '',
    className,
    prefix,
    suffix,
    shouldShowSarSymbol = false,
    freePriceOrText,
    ...props
}: PriceProps) => {
    const formattedPrice = price != null ? formatPriceValue(price) || String(price) : undefined;
    const formattedOldPrice = formatPriceValue(oldPrice) || oldPrice || '0.00';
    return (
        <div className={cn(styles.price, { [styles.start]: isStartAligned }, className)} {...props}>
            {includedLabel ? (
                <b
                    className={cn({
                        [styles.free]: free,
                        [styles.free]: (free || formattedOldPrice) === '0.00' ? false : true,
                    })}
                >
                    {includedLabel}
                </b>
            ) : (
                <>
                    {prefix && <i>{prefix}</i>}
                    <span className={styles.unwinded}>
                        {/* || currency === 'SAR' */}
                        {shouldShowSarSymbol && <FontIcon className={styles.sar} name="sar" />}
                        <b
                            className={cn({
                                [styles.free]: free,
                                [styles.free]: (free || formattedOldPrice) === '0.00' ? false : true,
                            })}
                        >
                            {formattedOldPrice === '"0.00"' ? formattedOldPrice : formattedPrice ?? price}
                        </b>
                        {(free || (formattedOldPrice === '0.00' ? false : true)) && (
                            <b>
                                {free
                                    ? freePriceOrText ?? '0.00'
                                    : formattedOldPrice === '0'
                                    ? '0.00'
                                    : formattedOldPrice}
                            </b>
                        )}
                    </span>
                    <i>
                        {/* Show code when: non-SAR, or SAR without symbol icon (text "SAR" instead of FontIcon) */}
                        {currency && (currency.toUpperCase() !== 'SAR' || !shouldShowSarSymbol) && currency}
                        {suffix}
                    </i>{' '}
                    {free && <span className="orange">{freeLabel}</span>}
                    {percentage && (
                        <div className={styles.description}>
                            <span className="orange">{`(${percentage}%)`}</span>
                        </div>
                    )}
                    {Array.isArray(description) ? (
                        description
                            .filter((item) => {
                                if (item === null || item === undefined) return false;
                                return typeof item === 'string' ? item.trim().length > 0 : true;
                            })
                            .map((item, index) => (
                                <div className={styles.description} key={index}>
                                    {item}
                                </div>
                            ))
                    ) : (
                        <div className={styles.description}>{description}</div>
                    )}
                </>
            )}
        </div>
    );
};
